import { useRef, useEffect, type Ref } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FS_MD, ICON_XS } from '../common/theme'
import { useDebouncedCallback } from '../common/useDebouncedCallback'
import { useUndoRedoHistory, type UndoRedoHandle, type UndoRedoState } from '../common/useUndoRedoHistory'

// Wait this long after the last keystroke before pushing the new HTML
// upward. Without it every typed character triggers a full parent
// re-render (AppContext.updateQuestion fans out to setCurrentProject +
// setCurrentQuestion which re-renders BidQuestion / RBT). 250ms feels
// instant to the user but keeps re-renders rare while typing.
const DEBOUNCE_MS = 250

// HTML-escape the four characters that matter when re-emitting plain
// text inside a <p>.
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// Convert clipboard plain text into the only HTML shape this editor
// accepts: a sequence of <p> blocks, with <br> for the soft line
// breaks inside each paragraph. Blank-line-separated chunks become
// distinct paragraphs.
function plainToParagraphHtml(text: string): string {
  if (!text) return ''
  return text
    .replace(/\r\n?/g, '\n')                  // normalise line endings
    .split(/\n\s*\n/)                          // blank lines → paragraph boundary
    .map(p => p.replace(/^\s+|\s+$/g, ''))     // trim each paragraph
    .filter(Boolean)
    .map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

// Cheap "is the DOM already clean?" check so the sanitize + caret
// dance only fires when something disallowed actually shows up
// (paste, IME compose finish, autocorrect, etc.). Plain typing stays
// a no-op.
function needsSanitize(root: HTMLElement): boolean {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null)
  let n: Node | null
  while ((n = walker.nextNode())) {
    const el = n as Element
    const tag = el.tagName.toLowerCase()
    if (tag !== 'p' && tag !== 'br') return true
    if (el.attributes.length > 0) return true
  }
  return false
}

// Walk the editor DOM and remove anything outside the {p, br} whitelist:
// unwrap disallowed elements (keep their text + descendants in place),
// and strip every attribute from the elements that survive. Run after
// `needsSanitize` confirms there's work to do.
function sanitizeEditor(root: HTMLElement): void {
  const all = Array.from(root.querySelectorAll('*'))
  for (const el of all) {
    const tag = el.tagName.toLowerCase()
    if (tag === 'p' || tag === 'br') {
      for (const attr of Array.from(el.attributes)) el.removeAttribute(attr.name)
      continue
    }
    // Unwrap - move children up, drop the element.
    const parent = el.parentNode
    if (!parent) continue
    while (el.firstChild) parent.insertBefore(el.firstChild, el)
    parent.removeChild(el)
  }
}

// Caret save/restore as a character offset from the start of the
// editor's text content. Lossy across <br> boundaries but close
// enough that the caret lands in the same logical spot after a
// sanitize pass - far better than letting the browser collapse it
// to the start of the editor.
function saveCaretOffset(root: HTMLElement): number | null {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  const range = sel.getRangeAt(0)
  if (!root.contains(range.endContainer)) return null
  const pre = range.cloneRange()
  pre.selectNodeContents(root)
  pre.setEnd(range.endContainer, range.endOffset)
  return pre.toString().length
}

function restoreCaretOffset(root: HTMLElement, offset: number): void {
  const sel = window.getSelection()
  if (!sel) return
  let remaining = offset
  let targetNode: Node | null = null
  let targetOffset = 0
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  let n: Node | null
  while ((n = walker.nextNode())) {
    const len = (n.textContent ?? '').length
    if (remaining <= len) {
      targetNode = n
      targetOffset = remaining
      break
    }
    remaining -= len
  }
  if (!targetNode) {
    // Past the end - drop the caret at the editor's end.
    const r = document.createRange()
    r.selectNodeContents(root)
    r.collapse(false)
    sel.removeAllRanges()
    sel.addRange(r)
    return
  }
  const r = document.createRange()
  r.setStart(targetNode, targetOffset)
  r.collapse(true)
  sel.removeAllRanges()
  sel.addRange(r)
}

interface TaQueryBarProps {
  htmlContent: string
  onChange: (html: string) => void
  editable?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
  id?: string
  // Imperative handle - pass a ref here to wire external Undo / Redo
  // buttons (in a parent toolbar) to this editor's history buffer. Mirrors
  // TaResponse's handleRef so callers don't have to learn two patterns.
  handleRef?: Ref<UndoRedoHandle>
  // Fires whenever the reachable history bounds flip. Parents store this
  // in state to enable / disable Undo / Redo toolbar buttons.
  onHistoryChange?: (state: UndoRedoState) => void
}

export default function TaQueryBar({
  htmlContent,
  onChange,
  editable = true,
  disabled = false,
  placeholder,
  id,
  handleRef,
  onHistoryChange,
}: TaQueryBarProps) {
  // String values use plain === for equality. Avoids JSON.stringify on
  // potentially long HTML strings on every keystroke.
  const { wrappedOnChange } = useUndoRedoHistory<string>({
    value: htmlContent,
    onChange,
    handleRef,
    onStateChange: onHistoryChange,
    equals: (a, b) => a === b,
  })
  const ref = useRef<HTMLDivElement>(null)
  // Tracks the last HTML we emitted upward so the htmlContent useEffect
  // can ignore the echo coming back through state - without this we'd
  // overwrite the DOM on every keystroke and reset the caret.
  const lastEmittedRef = useRef<string>(htmlContent)
  const hasContent = htmlContent.length > 0

  // Centralised debounce - same hook drives TaResponse. `schedule` is what
  // `onInput` calls; `flush` runs immediately on blur; `cancel` drops the
  // pending timer when we overwrite the editor (undo / clear).
  // `wrappedOnChange` is the history-aware version of `onChange` from
  // useUndoRedoHistory above - every edit that flows out through it
  // schedules a pause-aware snapshot for the Undo buffer.
  const debounced = useDebouncedCallback(() => {
    if (!ref.current) return
    const html = ref.current.innerHTML
    if (html === lastEmittedRef.current) return
    lastEmittedRef.current = html
    wrappedOnChange(html)
  }, DEBOUNCE_MS)

  // Sync prop into the DOM whenever they diverge. When the prop change
  // is the echo of our own emit, the user's keystroke already updated
  // the DOM so `innerHTML === htmlContent` is true and we skip - that's
  // what avoids clobbering the caret mid-type. External changes (initial
  // mount, parent overwrite, Undo elsewhere) take the write path.
  useEffect(() => {
    if (!ref.current) return
    if (ref.current.innerHTML === htmlContent) return
    lastEmittedRef.current = htmlContent
    ref.current.innerHTML = htmlContent
    // Same whitelist applies to external writes (initial mount,
    // parent overwrite, taskstream chunk, etc.) so stale span markup
    // from prior versions of the answer can't reappear here either.
    if (needsSanitize(ref.current)) sanitizeEditor(ref.current)
  }, [htmlContent])

  // Force the browser to emit <p> (not <div>) on Enter so the editor's
  // markup stays inside our whitelist. Set once per mount; harmless if
  // the browser ignores it.
  useEffect(() => {
    try { document.execCommand('defaultParagraphSeparator', false, 'p') } catch { /* ignore */ }
  }, [])

  const handleInput = () => {
    if (!ref.current) return
    // If something snuck in outside the {p, br} whitelist (autocorrect
    // span, paste path the browser bypassed our handler on, IME
    // commit, etc.) strip it on the spot. needsSanitize is cheap when
    // the DOM is already clean, so normal typing stays a no-op.
    if (needsSanitize(ref.current)) {
      const offset = saveCaretOffset(ref.current)
      sanitizeEditor(ref.current)
      if (offset != null) restoreCaretOffset(ref.current, offset)
    }
    debounced.schedule()
  }

  // Always flush pending text before the field loses focus so saves /
  // navigations see the latest value.
  const handleBlur = () => debounced.flush()

  // Intercept paste so the rich HTML the browser would otherwise drop
  // in (spans + inline styles + classes) never enters the editor.
  // Plain text is split into paragraphs and inserted as <p>/<br> only.
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!editable || disabled) return
    const text = e.clipboardData.getData('text/plain') ?? ''
    const html = plainToParagraphHtml(text)
    if (!html) return
    // execCommand is deprecated but remains the simplest cross-browser
    // way to insert HTML at the caret inside a contenteditable. Falling
    // back to a manual Selection/Range insert if it ever stops working
    // is a one-liner away.
    document.execCommand('insertHTML', false, html)
    debounced.schedule()
  }

  const handleClear = () => {
    debounced.cancel()
    lastEmittedRef.current = ''
    // Route through wrappedOnChange so a Clear is undoable.
    wrappedOnChange('')
    if (ref.current) ref.current.innerHTML = ''
  }

  const showClear = hasContent && editable && !disabled

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        // Hide the floating toolbar by default; reveal on hover or when
        // focus is inside the contenteditable. Pure CSS so it never
        // triggers a React re-render.
        '& [data-querybar-toolbar]': {
          opacity: 0,
          transition: 'opacity 0.15s ease',
        },
        '&:hover [data-querybar-toolbar], &:focus-within [data-querybar-toolbar]': {
          opacity: 1,
        },
      }}
    >
      <Box
        id={id}
        ref={ref}
        contentEditable={!disabled && editable}
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleBlur}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        data-editable={!disabled && editable ? 'true' : 'false'}
        sx={{
          fontSize: FS_MD,
          lineHeight: 1.4,
          minHeight: 30,
          outline: 'none',
          px: 1,
          py: 0.5,
          // py: 0.625,
          // Reserve room for the absolutely-positioned action buttons so
          // long content doesn't run underneath them.
          pr: showClear ? 4 : 1,
          color: 'text.primary',
          wordBreak: 'break-word',
          // Subtle frame so the user can see the editable area
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'transparent',
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
          // Disabled / read-only - flatten the frame so it doesn't read as editable
          '&[data-editable="false"]': {
            border: 'none',
            px: 0.5,
            py: 0.5,
            pr: showClear ? 4 : 0.5,
          },
          '&[data-editable="true"]:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
          '&[data-editable="true"]:focus': {
            borderColor: 'primary.main',
            backgroundColor: 'background.paper',
            boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}1f`,
          },
          '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            pt: 1,
            fontStyle: 'italic',
          },
          'P': {
            mt: 0.25,
            // border: BORDER_BLACK,
          },
        }}
      />
      {showClear && (
        <QueryBarToolbar
          showClear={showClear}
          onClear={handleClear}
        />
      )}
    </Box>
  )
}

interface QueryBarToolbarProps {
  showClear: boolean
  onClear: () => void
}

function QueryBarToolbar({ showClear, onClear }: QueryBarToolbarProps) {
  return (
    <Box
      data-querybar-toolbar
      sx={{
        position: 'absolute',
        top: 4,
        right: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.25,
        zIndex: 1,
        // Don't intercept clicks on the editable area - only on the
        // buttons themselves.
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >

      {showClear && (
        <Tooltip title="Clear">
          <IconButton size="small" onClick={onClear} sx={{ p: 0.25, bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}>
            <CloseIcon sx={{ fontSize: ICON_XS }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}


